// 吹き出し（段階 C の2 の 7g-3、2026-10-26。前は tippy.all.min.js＝tippy 3.3.0＋popper.js 1.x）。
// 操作のヒント（commonops.ts の viewTooltip）・はじめの確認（dialogs.ts の setFirstCheck）・dressup のポーズの注意（category.ts）で使う。
// 見た目はプラグインと同じ DOM・class・data-* 属性（.tippy-popper > .tippy-tooltip.〇〇-theme > .tippy-arrow・.tippy-content）と、
// tippy の CSS（tooltip-css.ts。前と同じく head の先頭に入れ、各メーカーの CSS のテーマがあとから上書きする）。
// 置き方は popper.js と同じ計算（基準の要素の真ん中に合わせて上下左右へ、文書の端から 5px 内に収める、矢印は基準の真ん中、位置は整数に丸める）。
// tippy・popper.js（Copyright Atomiks・Federico Zivolo, MIT License）の決まりを TS に移したもの。
// 出し方: 位置を決めてから data-state を visible に（CSS の transition で出る）、消すときは hidden にして transition が終わったら取り除く。
// 付けた要素には前と同じく tabindex="0"（フォーカスできない要素だけ）・出ている間 class tippy-active（interactive）・aria-describedby。
// sticky なら出ている間は毎コマ位置を合わせ直す。hideOnClick ならどこかを押すと消える（中を押しても消えないのは interactive の吹き出し）
import { TIPPY_CSS } from './tooltip-css.js';

export interface TooltipProps {
  content: string;
  /** 'top' | 'bottom' | 'left' | 'right' */
  placement?: string;
  animation?: string;
  /** 出す・消す時間（ms。[出す, 消す] でも） */
  duration?: number | number[];
  arrow?: boolean;
  arrowType?: 'sharp' | 'round';
  theme?: string;
  interactive?: boolean;
  sticky?: boolean;
  showOnInit?: boolean;
  hideOnClick?: boolean;
  zIndex?: number;
  distance?: number;
  maxWidth?: string | number;
  trigger?: string;
  /** ほかの tippy の設定（使っていないもの）は受け取って無視する */
  [name: string]: unknown;
  onHide?: () => void;
  onHidden?: () => void;
}

export interface Tooltip {
  reference: Element;
  popper: HTMLElement;
  show(duration?: number): void;
  hide(duration?: number): void;
  destroy(): void;
}
type Ref = Element & { _tippy?: Tooltip };

const DEFAULTS = { placement: 'top', animation: 'shift-away', duration: [325, 275] as number | number[], arrow: false, arrowType: 'sharp', theme: 'dark', interactive: false, sticky: false, showOnInit: false, hideOnClick: true, zIndex: 9999, distance: 10, maxWidth: '' as string | number, updateDuration: 200 };

let cssInjected = false;
function injectCss(): void {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.type = 'text/css';
  style.textContent = TIPPY_CSS;
  const head = document.head;
  if (head.firstChild) head.insertBefore(style, head.firstChild);
  else head.appendChild(style);
}

const all: Set<TooltipImpl> = new Set();
let lastId = 0;
let docListener = false;
/** どこかを押したら、hideOnClick の吹き出しを全部消す（interactive の吹き出しの中は除く。tippy の onDocumentClick） */
function listenDocument(): void {
  if (docListener) return;
  docListener = true;
  document.addEventListener(
    'click',
    (ev) => {
      const t = ev.target as Element | null;
      const popper = t && t.closest ? (t.closest('.tippy-popper') as (HTMLElement & { _tippy?: TooltipImpl }) | null) : null;
      if (popper && popper._tippy && popper._tippy.props.interactive) return;
      for (const tip of Array.from(all)) if (tip.props.hideOnClick === true) tip.hide();
    },
    true,
  );
}

// ---- 置き方（popper.js 1.x と同じ計算） ----

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}
const withEdges = (r: { top: number; left: number; width: number; height: number }): Rect => ({ ...r, right: r.left + r.width, bottom: r.top + r.height });
const cs = (el: Element): CSSStyleDeclaration => (el.ownerDocument.defaultView as Window).getComputedStyle(el);
function windowSizes(doc: Document): { width: number; height: number } {
  const b = doc.body, h = doc.documentElement;
  return {
    height: Math.max(b.offsetHeight, b.scrollHeight, h.clientHeight, h.offsetHeight, h.scrollHeight),
    width: Math.max(b.offsetWidth, b.scrollWidth, h.clientWidth, h.offsetWidth, h.scrollWidth),
  };
}
/** 要素の画面の上の四角（popper.js の getBoundingClientRect。HTML は文書の大きさ、スクロールバーの分を除く） */
function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect();
  const out = { left: r.left, top: r.top, width: r.right - r.left, height: r.bottom - r.top };
  const sizes = el.nodeName === 'HTML' ? windowSizes(el.ownerDocument) : null;
  const he = el as HTMLElement;
  const width = (sizes && sizes.width) || he.clientWidth || NaN;
  const height = (sizes && sizes.height) || he.clientHeight || NaN;
  let horiz = he.offsetWidth - width, vert = he.offsetHeight - height;
  if (horiz || vert) {
    const st = cs(el);
    horiz -= parseFloat(st.borderLeftWidth) + parseFloat(st.borderRightWidth);
    vert -= parseFloat(st.borderTopWidth) + parseFloat(st.borderBottomWidth);
    out.width -= horiz;
    out.height -= vert;
  }
  return withEdges(out);
}
/** 文書（HTML）から見た四角（popper.js の getOffsetRectRelativeToArbitraryNode。親は HTML） */
function rectInDocument(el: Element): Rect & { marginTop: number; marginLeft: number } {
  const html = el.ownerDocument.documentElement;
  const c = rectOf(el), p = rectOf(html), st = cs(html);
  const bt = parseFloat(st.borderTopWidth), bl = parseFloat(st.borderLeftWidth);
  const o = withEdges({ top: c.top - p.top - bt, left: c.left - p.left - bl, width: c.width, height: c.height });
  const mt = parseFloat(st.marginTop), ml = parseFloat(st.marginLeft);
  return { ...o, top: o.top - (bt - mt), bottom: o.bottom - (bt - mt), left: o.left - (bl - ml), right: o.right - (bl - ml), marginTop: mt, marginLeft: ml };
}
function outerSize(el: HTMLElement): { width: number; height: number } {
  const st = cs(el);
  return {
    width: el.offsetWidth + parseFloat(st.marginLeft || '0') + parseFloat(st.marginRight || '0'),
    height: el.offsetHeight + parseFloat(st.marginTop || '0') + parseFloat(st.marginBottom || '0'),
  };
}
/** 吹き出しの位置（文書の座標）と矢印の位置を出す */
function computePosition(reference: Element, popper: HTMLElement, arrow: HTMLElement | null, placement: string): { x: number; y: number; arrow: number | null } {
  const ref = rectInDocument(reference);
  const size = outerSize(popper);
  const horiz = placement === 'left' || placement === 'right';
  const main = horiz ? 'top' : 'left', second = horiz ? 'left' : 'top';
  const meas = horiz ? 'height' : 'width', secondMeas = horiz ? 'width' : 'height';
  const opposite: Record<string, 'right' | 'bottom' | 'left' | 'top'> = { left: 'right', top: 'bottom', right: 'left', bottom: 'top' };
  let pop: Rect = withEdges({ width: size.width, height: size.height, top: 0, left: 0 });
  const o: Record<string, number> = { width: size.width, height: size.height };
  o[main] = ref[main] + ref[meas] / 2 - size[meas] / 2;
  o[second] = placement === second ? ref[second] - size[secondMeas] : ref[opposite[second]];
  pop = withEdges({ top: o.top, left: o.left, width: o.width, height: o.height });
  // 文書の端から 5px 内に収める（preventOverflow。boundaries は文書全体）
  const doc = rectInDocument(reference.ownerDocument.documentElement);
  const ws = windowSizes(reference.ownerDocument);
  const bounds = { top: doc.top - doc.marginTop + 5, left: doc.left - doc.marginLeft + 5, bottom: ws.height + doc.top - 5, right: ws.width + doc.left - 5 };
  // （popper.js と同じく、右端・下端は最初の値のまま比べる）
  pop = withEdges(pop);
  for (const side of ['left', 'right', 'top', 'bottom'] as const) {
    if (side === 'left' || side === 'top') {
      if (pop[side] < bounds[side]) pop = { ...pop, [side]: Math.max(pop[side], bounds[side]) };
    } else {
      const mainSide = side === 'right' ? 'left' : 'top';
      if (pop[side] > bounds[side]) pop = { ...pop, [mainSide]: Math.min(pop[mainSide], bounds[side] - (side === 'right' ? pop.width : pop.height)) };
    }
  }
  // 基準と重なるように（keepTogether。比べるのはこの処理の初めの値）
  pop = withEdges(pop);
  {
    const tb = !horiz, endS = tb ? 'right' : 'bottom', startS = tb ? 'left' : 'top', lenS = tb ? 'width' : 'height';
    const r0 = pop;
    if (r0[endS] < Math.floor(ref[startS])) pop = { ...pop, [startS]: Math.floor(ref[startS]) - r0[lenS] };
    if (r0[startS] > Math.floor(ref[endS])) pop = { ...pop, [startS]: Math.floor(ref[endS]) };
  }
  pop = withEdges(pop);
  // 矢印（基準の真ん中。吹き出しの中に収める。比べるのはこの処理の初めの値）
  let arrowPos: number | null = null;
  if (arrow) {
    const vertical = horiz;
    const len = vertical ? 'height' : 'width', side = vertical ? 'top' : 'left', Side = vertical ? 'Top' : 'Left', endS = vertical ? 'bottom' : 'right';
    const asz = outerSize(arrow)[len];
    const n0 = pop;
    if (ref[endS] - asz < n0[side]) pop = { ...pop, [side]: pop[side] - (n0[side] - (ref[endS] - asz)) };
    if (ref[side] + asz > n0[endS]) pop = { ...pop, [side]: pop[side] + (ref[side] + asz - n0[endS]) };
    pop = withEdges(pop);
    const center = ref[side] + ref[len] / 2 - asz / 2;
    const pst = cs(popper) as unknown as Record<string, string>;
    let v = center - pop[side] - parseFloat(pst['margin' + Side]) - parseFloat(pst['border' + Side + 'Width']);
    v = Math.max(Math.min(n0[len] - asz, v), 0);
    arrowPos = Math.round(v);
  }
  // 位置を整数に（popper.js の computeStyle。幅の偶奇で left の丸め方を変える）
  const pw = Math.round(pop.width), rw = Math.round(ref.width);
  const roundX = horiz || pw % 2 === rw % 2 ? Math.round : Math.floor;
  const x = roundX(rw % 2 === 1 && pw % 2 === 1 ? pop.left - 1 : pop.left);
  const y = Math.round(pop.top);
  return { x, y, arrow: arrowPos };
}

// ---- 吹き出し ----

class TooltipImpl implements Tooltip {
  reference: Ref;
  popper: HTMLElement & { _tippy?: TooltipImpl };
  tooltip: HTMLElement;
  content: HTMLElement;
  arrow: HTMLElement | null;
  props: typeof DEFAULTS & TooltipProps;
  visible = false;
  mounted = false;
  destroyed = false;
  private endListener: ((ev: TransitionEvent) => void) | null = null;
  private stickyFrame = 0;

  constructor(reference: Ref, props: TooltipProps) {
    this.reference = reference;
    this.props = { ...DEFAULTS, ...props } as typeof DEFAULTS & TooltipProps;
    if (this.props.arrow) (this.props as Record<string, unknown>).animateFill = false;
    const p = this.props;
    const id = ++lastId;
    const popper = document.createElement('div') as HTMLElement & { _tippy?: TooltipImpl };
    popper.className = 'tippy-popper';
    popper.setAttribute('role', 'tooltip');
    popper.id = 'tippy-' + id;
    popper.style.zIndex = String(p.zIndex);
    const tooltip = document.createElement('div');
    tooltip.className = 'tippy-tooltip';
    tooltip.style.maxWidth = p.maxWidth + (typeof p.maxWidth === 'number' ? 'px' : '');
    tooltip.setAttribute('data-size', 'regular');
    tooltip.setAttribute('data-animation', String(p.animation));
    tooltip.setAttribute('data-state', 'hidden');
    String(p.theme)
      .split(' ')
      .forEach((t) => tooltip.classList.add(t + '-theme'));
    const content = document.createElement('div');
    content.className = 'tippy-content';
    content.setAttribute('data-state', 'hidden');
    if (p.interactive) {
      popper.setAttribute('tabindex', '-1');
      tooltip.setAttribute('data-interactive', '');
    }
    let arrow: HTMLElement | null = null;
    if (p.arrow) {
      arrow = document.createElement('div');
      if (p.arrowType === 'round') {
        arrow.className = 'tippy-roundarrow';
        arrow.innerHTML =
          '<svg viewBox="0 0 24 8" xmlns="http://www.w3.org/2000/svg"><path d="M3 8s2.021-.015 5.253-4.218C9.584 2.051 10.797 1.007 12 1c1.203-.007 2.416 1.035 3.761 2.782C19.012 8.005 21 8 21 8H3z"/></svg>';
      } else arrow.className = 'tippy-arrow';
      tooltip.appendChild(arrow);
    }
    content.innerHTML = p.content;
    tooltip.appendChild(content);
    popper.appendChild(tooltip);
    popper._tippy = this;
    this.popper = popper;
    this.tooltip = tooltip;
    this.content = content;
    this.arrow = arrow;
    // フォーカスできない要素には tabindex="0"（tippy の a11y）
    if (!reference.matches('a[href],area[href],button,details,input,textarea,select,iframe,[tabindex]') || reference.hasAttribute('disabled')) reference.setAttribute('tabindex', '0');
    reference._tippy = this;
    all.add(this);
    listenDocument();
    if (p.showOnInit) this.show();
  }

  private durations(els: (HTMLElement | null)[], ms: number): void {
    for (const el of els) if (el) el.style.transitionDuration = ms + 'ms';
  }
  private states(state: string): void {
    this.tooltip.setAttribute('data-state', state);
    this.content.setAttribute('data-state', state);
  }
  /** transition が終わったら（時間 0 ならすぐ）。前の待ちは取り消す */
  private afterTransition(ms: number, fn: () => void): void {
    if (this.endListener) this.tooltip.removeEventListener('transitionend', this.endListener);
    this.endListener = null;
    if (ms === 0) return fn();
    const l = (ev: TransitionEvent): void => {
      if (ev.target !== this.tooltip) return;
      this.tooltip.removeEventListener('transitionend', l);
      this.endListener = null;
      fn();
    };
    this.endListener = l;
    this.tooltip.addEventListener('transitionend', l);
  }
  private place(): void {
    const p = this.props;
    this.popper.style.position = 'absolute';
    const pos = computePosition(this.reference, this.popper, this.arrow, String(p.placement));
    this.popper.setAttribute('x-placement', String(p.placement));
    this.popper.style.willChange = 'transform';
    this.popper.style.top = '0px';
    this.popper.style.left = '0px';
    this.popper.style.transform = 'translate3d(' + pos.x + 'px, ' + pos.y + 'px, 0)';
    if (this.arrow && pos.arrow !== null) {
      if (p.placement === 'left' || p.placement === 'right') this.arrow.style.top = pos.arrow + 'px';
      else this.arrow.style.left = pos.arrow + 'px';
    }
    // tippy: 吹き出しを distance の分だけ離す（CSS が visible で 10px 寄せる分を足す）
    const t = this.tooltip.style;
    t.top = '';
    t.bottom = '';
    t.left = '';
    t.right = '';
    (t as unknown as Record<string, string>)[String(p.placement)] = -((p.distance as number) - 10) + 'px';
  }

  show(duration?: number): void {
    if (this.destroyed || !document.documentElement.contains(this.reference)) return;
    const d = duration !== undefined ? duration : Array.isArray(this.props.duration) ? this.props.duration[0] : (this.props.duration as number);
    this.popper.style.visibility = 'visible';
    this.visible = true;
    this.durations([this.popper, this.tooltip], 0);
    if (!this.mounted) {
      document.body.appendChild(this.popper);
      this.mounted = true;
    }
    this.place();
    void this.popper.offsetHeight;
    this.durations([this.tooltip, this.content], d);
    if (this.props.interactive) this.reference.classList.add('tippy-active');
    if (this.props.sticky) this.stick();
    this.states('visible');
    this.afterTransition(d, () => {
      this.reference.setAttribute('aria-describedby', this.popper.id);
    });
  }
  /** 出ている間は毎コマ位置を合わせ直す（sticky。位置の変わり方は updateDuration で滑らかに） */
  private stick(): void {
    this.durations([this.popper], this.props.updateDuration);
    const loop = (): void => {
      if (!this.mounted) {
        this.durations([this.popper], 0);
        return;
      }
      this.place();
      this.stickyFrame = requestAnimationFrame(loop);
    };
    cancelAnimationFrame(this.stickyFrame);
    this.stickyFrame = requestAnimationFrame(loop);
  }

  hide(duration?: number): void {
    if (this.destroyed || !this.visible) return;
    const d = duration !== undefined ? duration : Array.isArray(this.props.duration) ? this.props.duration[1] : (this.props.duration as number);
    if (this.props.onHide) this.props.onHide();
    if (this.props.interactive) this.reference.classList.remove('tippy-active');
    this.popper.style.visibility = 'hidden';
    this.visible = false;
    this.durations([this.tooltip, this.content], d);
    this.states('hidden');
    this.afterTransition(d, () => {
      if (this.visible || !this.mounted) return;
      this.reference.removeAttribute('aria-describedby');
      if (this.popper.parentNode) this.popper.parentNode.removeChild(this.popper);
      this.mounted = false;
      cancelAnimationFrame(this.stickyFrame);
      if (this.props.onHidden) this.props.onHidden();
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    if (this.mounted) this.hide(0);
    delete this.reference._tippy;
    all.delete(this);
    this.destroyed = true;
  }
}

/** 吹き出しを付ける（tippy(セレクター, 設定)。合う要素ごとに 1 つ。もう付いている要素には付けない） */
export function tooltip(target: string | Element, props: TooltipProps): void {
  injectCss();
  const list: Ref[] = typeof target === 'string' ? (Array.prototype.slice.call(document.querySelectorAll(target)) as Ref[]) : [target as Ref];
  for (const el of list) {
    if (el._tippy) continue;
    new TooltipImpl(el, props);
  }
}
